import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

// Vordefinierte Geräte-Vorlagen
const DEVICE_PRESETS = [
  { name: "Standard", smartphone_max: 640, tablet_max: 1024 },
  { name: "Groß (für größere Smartphones)", smartphone_max: 768, tablet_max: 1280 },
  { name: "Kompakt", smartphone_max: 480, tablet_max: 768 },
];

export function DeviceBreakpointsConfig({ authToken }) {
  const [breakpoints, setBreakpoints] = useState({ smartphone_max: 640, tablet_max: 1024 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [message, setMessage] = useState(null);
  
  // Vorschau-Status
  const [previewMode, setPreviewMode] = useState(null); // null, "smartphone", "tablet", "desktop"
  const [previewWidth, setPreviewWidth] = useState(null);
  const iframeRef = useRef(null);

  const authHeaders = useCallback(
    () => ({ headers: { Authorization: `Bearer ${authToken}` } }),
    [authToken]
  );

  // Einstellungen laden
  useEffect(() => {
    const loadBreakpoints = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API_BASE}/api/device-breakpoints`, authHeaders());
        setBreakpoints(res.data);
      } catch (error) {
        console.error("Fehler beim Laden der Breakpoints:", error);
      }
      setLoading(false);
    };
    loadBreakpoints();
  }, [authHeaders]);

  const handleChange = (field, value) => {
    const numValue = parseInt(value, 10);
    if (!isNaN(numValue) && numValue > 0) {
      setBreakpoints((prev) => ({ ...prev, [field]: numValue }));
      setHasChanges(true);
      setMessage(null);
    }
  };

  const applyPreset = (preset) => {
    setBreakpoints({ smartphone_max: preset.smartphone_max, tablet_max: preset.tablet_max });
    setHasChanges(true);
    setMessage(null);
  };

  const handleSave = async () => {
    // Validierung
    if (breakpoints.smartphone_max >= breakpoints.tablet_max) {
      setMessage({ type: "error", text: "Smartphone-Breakpoint muss kleiner als Tablet-Breakpoint sein" });
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${API_BASE}/api/device-breakpoints`, breakpoints, authHeaders());
      setMessage({ type: "success", text: "Breakpoints gespeichert! Die Seite wird neu geladen..." });
      setHasChanges(false);
      setTimeout(() => window.location.reload(), 1500);
    } catch (error) {
      console.error("Fehler beim Speichern:", error);
      setMessage({ type: "error", text: "Fehler beim Speichern der Breakpoints" });
    }
    setSaving(false);
  };

  const handleReset = async () => {
    if (!window.confirm("Möchten Sie die Breakpoints auf die Standardwerte zurücksetzen?")) {
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API_BASE}/api/device-breakpoints/reset`, {}, authHeaders());
      setBreakpoints(res.data);
      setMessage({ type: "success", text: "Breakpoints zurückgesetzt! Die Seite wird neu geladen..." });
      setHasChanges(false);
      setTimeout(() => window.location.reload(), 1500);
    } catch (error) {
      console.error("Fehler beim Zurücksetzen:", error);
      setMessage({ type: "error", text: "Fehler beim Zurücksetzen der Breakpoints" });
    }
    setSaving(false);
  };

  // Vorschau-Funktionen
  const openPreview = (mode) => {
    let width;
    switch (mode) {
      case "smartphone":
        width = Math.min(breakpoints.smartphone_max, 414); // Typische Smartphone-Breite
        break;
      case "tablet":
        width = Math.min(breakpoints.tablet_max, 768); // Typische Tablet-Breite
        break;
      case "desktop":
        width = 1280;
        break;
      default:
        width = null;
    }
    setPreviewMode(mode);
    setPreviewWidth(width);
  };

  const closePreview = () => {
    setPreviewMode(null);
    setPreviewWidth(null);
  };

  // Benutzerdefinierte Breite setzen
  const handleCustomWidth = (e) => {
    const value = parseInt(e.target.value, 10);
    if (!isNaN(value) && value >= 320 && value <= 1920) {
      setPreviewWidth(value);
      // Modus basierend auf Breite aktualisieren
      if (value <= breakpoints.smartphone_max) {
        setPreviewMode("smartphone");
      } else if (value <= breakpoints.tablet_max) {
        setPreviewMode("tablet");
      } else {
        setPreviewMode("desktop");
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-violet-600 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="device-breakpoints-config">
      {/* Header */}
      <div className="rounded-xl bg-gradient-to-r from-blue-50 to-white p-4 shadow-sm">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h3 className="font-bold text-slate-900" style={{ fontSize: 'var(--font-h3-size)', fontWeight: 'var(--font-h3-weight)' }}>
              Geräte-Breakpoints
            </h3>
            <p className="mt-1 text-slate-500" style={{ fontSize: 'var(--font-body-size)' }}>
              Definieren Sie, ab welcher Bildschirmbreite ein Gerät als Smartphone, Tablet oder Desktop gilt.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleReset}
              disabled={saving}
              className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              style={{ fontSize: 'var(--font-body-size)', fontWeight: 'var(--font-body-weight)' }}
            >
              Standard
            </button>
            <button
              onClick={handleSave}
              disabled={saving || !hasChanges}
              className="rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-500 disabled:opacity-50"
              style={{ fontSize: 'var(--font-body-size)', fontWeight: 'var(--font-body-weight)' }}
            >
              {saving ? "Speichern..." : "Speichern"}
            </button>
          </div>
        </div>

        {/* Status-Meldung */}
        {message && (
          <div
            className={`mt-3 rounded-lg p-3 ${
              message.type === "success"
                ? "bg-emerald-100 text-emerald-700"
                : "bg-rose-100 text-rose-700"
            }`}
            style={{ fontSize: 'var(--font-body-size)' }}
          >
            {message.text}
          </div>
        )}
      </div>

      {/* Vorlagen */}
      <div className="rounded-xl bg-white p-4 shadow-sm">
        <h4 className="mb-3 text-slate-900" style={{ fontSize: 'var(--font-body-size)', fontWeight: 'var(--font-label-weight)' }}>
          Schnellauswahl
        </h4>
        <div className="flex flex-wrap gap-2">
          {DEVICE_PRESETS.map((preset) => (
            <button
              key={preset.name}
              onClick={() => applyPreset(preset)}
              className={`rounded-full px-4 py-2 transition-all ${
                breakpoints.smartphone_max === preset.smartphone_max && breakpoints.tablet_max === preset.tablet_max
                  ? "bg-blue-600 text-white"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
              style={{ fontSize: 'var(--font-body-size)', fontWeight: 'var(--font-body-weight)' }}
            >
              {preset.name}
            </button>
          ))}
        </div>
      </div>

      {/* Breakpoint-Eingaben */}
      <div className="rounded-xl bg-white p-4 shadow-sm">
        <h4 className="mb-4 text-slate-900" style={{ fontSize: 'var(--font-body-size)', fontWeight: 'var(--font-label-weight)' }}>
          Breakpoints konfigurieren
        </h4>
        <div className="grid gap-6 sm:grid-cols-2">
          <div>
            <label className="mb-2 block text-slate-700" style={{ fontSize: 'var(--font-body-size)', fontWeight: 'var(--font-body-weight)' }}>
              📱 Smartphone (max. Breite)
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={breakpoints.smartphone_max}
                onChange={(e) => handleChange("smartphone_max", e.target.value)}
                min="320"
                max="1200"
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 focus:border-blue-500 focus:outline-none"
                style={{ fontSize: 'var(--font-body-size)' }}
              />
              <span className="text-slate-500" style={{ fontSize: 'var(--font-body-size)' }}>px</span>
            </div>
            <p className="mt-1 text-slate-400" style={{ fontSize: 'var(--font-small-size)' }}>
              Bildschirme bis zu dieser Breite werden als Smartphone behandelt
            </p>
          </div>
          <div>
            <label className="mb-2 block text-slate-700" style={{ fontSize: 'var(--font-body-size)', fontWeight: 'var(--font-body-weight)' }}>
              📲 Tablet (max. Breite)
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={breakpoints.tablet_max}
                onChange={(e) => handleChange("tablet_max", e.target.value)}
                min="600"
                max="1600"
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 focus:border-blue-500 focus:outline-none"
                style={{ fontSize: 'var(--font-body-size)' }}
              />
              <span className="text-slate-500" style={{ fontSize: 'var(--font-body-size)' }}>px</span>
            </div>
            <p className="mt-1 text-slate-400" style={{ fontSize: 'var(--font-small-size)' }}>
              Bildschirme bis zu dieser Breite werden als Tablet behandelt (darüber = Desktop)
            </p>
          </div>
        </div>
      </div>

      {/* Visualisierung */}
      <div className="rounded-xl bg-white p-4 shadow-sm">
        <h4 className="mb-4 text-slate-900" style={{ fontSize: 'var(--font-body-size)', fontWeight: 'var(--font-label-weight)' }}>
          Vorschau der Bereiche
        </h4>
        <div className="relative h-12 rounded-lg bg-slate-100 overflow-hidden">
          {/* Smartphone-Bereich */}
          <div 
            className="absolute left-0 top-0 h-full bg-orange-200 flex items-center justify-center text-orange-800"
            style={{ width: `${(breakpoints.smartphone_max / 1920) * 100}%`, fontSize: 'var(--font-small-size)', fontWeight: 'var(--font-body-weight)' }}
          >
            📱 Smartphone
          </div>
          {/* Tablet-Bereich */}
          <div 
            className="absolute top-0 h-full bg-blue-200 flex items-center justify-center text-blue-800"
            style={{ 
              left: `${(breakpoints.smartphone_max / 1920) * 100}%`,
              width: `${((breakpoints.tablet_max - breakpoints.smartphone_max) / 1920) * 100}%`,
              fontSize: 'var(--font-small-size)', 
              fontWeight: 'var(--font-body-weight)'
            }}
          >
            📲 Tablet
          </div>
          {/* Desktop-Bereich */}
          <div 
            className="absolute top-0 h-full bg-emerald-200 flex items-center justify-center text-emerald-800"
            style={{ 
              left: `${(breakpoints.tablet_max / 1920) * 100}%`,
              right: 0,
              fontSize: 'var(--font-small-size)', 
              fontWeight: 'var(--font-body-weight)'
            }}
          >
            🖥️ Desktop
          </div>
        </div>
        <div className="mt-2 flex justify-between text-slate-500" style={{ fontSize: 'var(--font-small-size)' }}>
          <span>0px</span>
          <span>{breakpoints.smartphone_max}px</span>
          <span>{breakpoints.tablet_max}px</span>
          <span>1920px+</span>
        </div>
      </div>

      {/* Interaktive Vorschau */}
      <div className="rounded-xl bg-gradient-to-r from-violet-50 to-white p-4 shadow-sm">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div>
            <h4 className="text-slate-900" style={{ fontSize: 'var(--font-body-size)', fontWeight: 'var(--font-label-weight)' }}>
              🔍 Ansicht testen
            </h4>
            <p className="mt-1 text-slate-500" style={{ fontSize: 'var(--font-small-size)' }}>
              Testen Sie, wie die App auf verschiedenen Geräten aussieht
            </p>
          </div>
        </div>

        {/* Geräte-Auswahl Buttons */}
        <div className="flex flex-wrap gap-2 mb-4">
          <button
            onClick={() => openPreview("smartphone")}
            className={`flex items-center gap-2 rounded-lg px-4 py-2.5 transition-all ${
              previewMode === "smartphone"
                ? "bg-orange-500 text-white shadow-md"
                : "bg-white text-slate-700 border border-slate-200 hover:bg-orange-50 hover:border-orange-300"
            }`}
            style={{ fontSize: 'var(--font-body-size)', fontWeight: 'var(--font-body-weight)' }}
          >
            <span className="text-lg">📱</span>
            <span>Smartphone</span>
            <span className="text-xs opacity-75">({breakpoints.smartphone_max}px)</span>
          </button>
          
          <button
            onClick={() => openPreview("tablet")}
            className={`flex items-center gap-2 rounded-lg px-4 py-2.5 transition-all ${
              previewMode === "tablet"
                ? "bg-blue-500 text-white shadow-md"
                : "bg-white text-slate-700 border border-slate-200 hover:bg-blue-50 hover:border-blue-300"
            }`}
            style={{ fontSize: 'var(--font-body-size)', fontWeight: 'var(--font-body-weight)' }}
          >
            <span className="text-lg">📲</span>
            <span>Tablet</span>
            <span className="text-xs opacity-75">({breakpoints.tablet_max}px)</span>
          </button>
          
          <button
            onClick={() => openPreview("desktop")}
            className={`flex items-center gap-2 rounded-lg px-4 py-2.5 transition-all ${
              previewMode === "desktop"
                ? "bg-emerald-500 text-white shadow-md"
                : "bg-white text-slate-700 border border-slate-200 hover:bg-emerald-50 hover:border-emerald-300"
            }`}
            style={{ fontSize: 'var(--font-body-size)', fontWeight: 'var(--font-body-weight)' }}
          >
            <span className="text-lg">🖥️</span>
            <span>Desktop</span>
            <span className="text-xs opacity-75">(1280px)</span>
          </button>

          {previewMode && (
            <button
              onClick={closePreview}
              className="flex items-center gap-2 rounded-lg px-4 py-2.5 bg-slate-100 text-slate-600 hover:bg-slate-200 transition-all ml-auto"
              style={{ fontSize: 'var(--font-body-size)', fontWeight: 'var(--font-body-weight)' }}
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              Schließen
            </button>
          )}
        </div>

        {/* Vorschau-Bereich */}
        {previewMode && (
          <div className="space-y-3">
            {/* Breiten-Kontrolle */}
            <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-slate-200">
              <label className="text-slate-600 whitespace-nowrap" style={{ fontSize: 'var(--font-body-size)' }}>
                Breite anpassen:
              </label>
              <input
                type="range"
                min="320"
                max="1920"
                value={previewWidth || 375}
                onChange={handleCustomWidth}
                className="flex-1 h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-violet-600"
              />
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  min="320"
                  max="1920"
                  value={previewWidth || 375}
                  onChange={handleCustomWidth}
                  className="w-20 rounded border border-slate-200 px-2 py-1 text-center focus:border-violet-500 focus:outline-none"
                  style={{ fontSize: 'var(--font-body-size)' }}
                />
                <span className="text-slate-500" style={{ fontSize: 'var(--font-body-size)' }}>px</span>
              </div>
            </div>

            {/* Geräte-Typ Anzeige */}
            <div className="flex items-center gap-2 text-sm">
              <span className="text-slate-500">Erkannter Gerätetyp:</span>
              {previewWidth <= breakpoints.smartphone_max && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-medium">
                  📱 Smartphone
                </span>
              )}
              {previewWidth > breakpoints.smartphone_max && previewWidth <= breakpoints.tablet_max && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                  📲 Tablet
                </span>
              )}
              {previewWidth > breakpoints.tablet_max && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium">
                  🖥️ Desktop
                </span>
              )}
            </div>

            {/* iframe Vorschau */}
            <div 
              className="relative bg-slate-800 rounded-xl p-4 overflow-hidden"
              style={{ minHeight: "500px" }}
            >
              {/* Geräte-Rahmen Simulation */}
              <div 
                className="mx-auto bg-slate-900 rounded-2xl p-2 shadow-2xl transition-all duration-300"
                style={{ 
                  width: `${Math.min(previewWidth + 16, 1300)}px`,
                  maxWidth: "100%"
                }}
              >
                {/* Geräte-Header (wie ein Browser/Gerät) */}
                <div className="flex items-center justify-between px-3 py-2 bg-slate-800 rounded-t-xl">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-500"></div>
                    <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  </div>
                  <div className="flex-1 mx-4">
                    <div className="bg-slate-700 rounded px-3 py-1 text-xs text-slate-400 text-center truncate">
                      {window.location.origin} ({previewWidth}px)
                    </div>
                  </div>
                  <div className="text-xs text-slate-500">
                    {previewMode === "smartphone" && "📱"}
                    {previewMode === "tablet" && "📲"}
                    {previewMode === "desktop" && "🖥️"}
                  </div>
                </div>
                
                {/* iframe Container */}
                <div 
                  className="bg-white overflow-hidden rounded-b-xl"
                  style={{ height: "450px" }}
                >
                  <iframe
                    ref={iframeRef}
                    src={window.location.origin}
                    title="Geräte-Vorschau"
                    className="border-0"
                    style={{ 
                      width: `${previewWidth}px`,
                      height: "450px",
                      transform: previewWidth > 800 ? `scale(${Math.min(800 / previewWidth, 1)})` : "none",
                      transformOrigin: "top left"
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Hinweis */}
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800" style={{ fontSize: 'var(--font-small-size)' }}>
              <strong>Hinweis:</strong> Die Vorschau zeigt die App in der gewählten Breite. Sie müssen sich im iframe ggf. erneut anmelden, um alle Funktionen zu testen.
            </div>
          </div>
        )}
      </div>

      {/* Info-Box */}
      <div className="rounded-lg bg-blue-50 p-3 text-blue-700" style={{ fontSize: 'var(--font-body-size)' }}>
        <strong>Hinweis:</strong> Die Berechtigungen für Smartphone und Tablet können Sie separat unter "Rollen & Berechtigungen" konfigurieren.
      </div>
    </div>
  );
}
